import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedPages from '@/components/base/RelatedPages';

const envFeatures = [
  {
    icon: 'ri-building-4-line',
    title: '潔淨生產車間',
    desc: '全程溫溼度監控，防止交叉污染，確保每批次生產品質穩定一致',
  },
  {
    icon: 'ri-flask-line',
    title: '多樣劑型機台',
    desc: '支援不同劑型需求，從錠劑到液劑都能協助生產，定期清潔檢測機台環境，管控產品品質',
  },
  {
    icon: 'ri-test-tube-line',
    title: '品管檢驗室',
    desc: '每批次出廠皆具檢驗報告，也提供其他殘留檢驗，把控產品的高安全性',
  },
  {
    icon: 'ri-store-2-line',
    title: '溫控倉儲物流',
    desc: '依原料與成品特性分區溫控存放，確保原料新鮮度與成品安全',
  },
];

const qcProcess = [
  { step: '01', icon: 'ri-truck-line', title: '原料進廠驗收', desc: '每批原料入廠進行外觀、成分含量、微生物、重金屬等多項檢測，不合格批次一律退回。' },
  { step: '02', icon: 'ri-settings-3-line', title: '製程監控管理', desc: '生產全程監控重量差異、崩散度、硬度等關鍵品質指標，異常立即停線處理。' },
  { step: '03', icon: 'ri-eye-line', title: '半成品中間管控', desc: '每個製程節點均進行中間管控檢查，確保半成品符合規格後方可進入下一製程。' },
  { step: '04', icon: 'ri-checkbox-circle-line', title: '成品全項檢驗', desc: '出貨前進行完整成品檢驗，包含微生物、農藥、重金屬、標示符合性等全面查核。' },
  { step: '05', icon: 'ri-file-check-line', title: '出具檢驗報告', desc: '每批次附完整 Certificate of Analysis（CoA），供客戶存檔及市場查核使用。' },
];

const certifications = [
  {
    name: 'HACCP 認證',
    fullName: '危害分析重要管制點',
    desc: '建立系統性危害分析流程，針對每個製程關鍵控制點（CCP）設定管制標準，主動預防食品安全危害。',
    icon: 'ri-scan-line',
    color: '#388e3c',
  },
  {
    name: 'ISO 22000',
    fullName: '食品安全管理系統',
    desc: '通過國際食品安全管理系統認證，整合 HACCP 原則與 ISO 管理系統，提供符合國際標準的食品安全保障。',
    icon: 'ri-shield-check-line',
    color: '#2e7d32',
  },
];

const docSupport = [
  { icon: 'ri-file-list-3-line', text: '批次生產記錄（Batch Record）' },
  { icon: 'ri-file-check-line', text: '出廠檢驗報告' },
  { icon: 'ri-file-paper-2-line', text: '原料檢驗報告 (CoA)' },
  { icon: 'ri-shield-check-line', text: '原料穩定性測試報告' },
  { icon: 'ri-global-line', text: '出口所需品質文件支援' },
  { icon: 'ri-building-line', text: 'SGS相關第三方檢驗協助' },
];

export default function ProductionContent() {
  return (
    <>
      {/* Hero Banner */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20GMP%20certified%20pharmaceutical%20manufacturing%20production%20line%20stainless%20steel%20equipment%20workers%20white%20uniform%20hairnet%20clean%20room%2C%20professional%20health%20supplement%20factory%20interior%20bright%20lighting&width=1920&height=420&seq=prod-hero-bg-01&orientation=landscape"
            alt="專業生產背景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <div className="mb-5">
              <div className="inline-block bg-black/8 backdrop-blur-sm rounded-lg px-3 py-1.5" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                <Breadcrumb
                  items={[
                    { label: '首頁', href: '/' },
                    { label: '關於康永', href: '/about' },
                    { label: '專業生產' },
                  ]}
                  textClassName="text-white"
                />
              </div>
            </div>
            <span className="inline-block bg-[#4CAF50]/30 border border-[#4CAF50]/40 text-white text-[0.875rem] font-semibold px-4 py-1.5 rounded-full mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.2)]">
              關於康永
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              專業生產
            </h1>
            <p className="text-gray-200 text-[0.9375rem] md:text-[1.0625rem] leading-[1.75] max-w-3xl md:whitespace-nowrap">
              以穩定製程、品質管理與食品安全認證，協助品牌打造安心可靠的保健產品。
            </p>
          </div>
        </div>
      </section>

      {/* Production environment */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              廠房設施
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">專業生產環境</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              符合 國際相關認證 規範標準，為每一分產品提供最安全的生產環境
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {envFeatures.map((f) => (
              <div key={f.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                  <i className={`${f.icon} text-[#4CAF50] text-xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{f.title}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{f.desc}</p>
              </div>
            ))}
          </div>
          {/* Facility image */}
          <div className="rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=wide%20panoramic%20view%20of%20clean%20pharmaceutical%20supplement%20manufacturing%20facility%20with%20modern%20equipment%20conveyors%20stainless%20steel%20tanks%20workers%20in%20white%20uniforms%2C%20GMP%20factory%20interior%20bright%20professional%20environment&width=1400&height=500&seq=prod-facility-wide-01&orientation=landscape"
              alt="康永生技廠房全景"
              className="w-full h-[320px] md:h-[420px] object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* QC Process */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              品管流程
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">五階段品質管理流程</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              從原料進廠到成品出貨，每個環節都有嚴格的品管機制把關
            </p>
          </div>
          <div className="relative">
            {/* Connector line desktop */}
            <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-green-100 z-0" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 relative z-10">
              {qcProcess.map((q) => (
                <div key={q.step} className="flex flex-col items-center text-center">
                  <div className="relative mb-5">
                    <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full border-2 border-[#4CAF50]">
                      <i className={`${q.icon} text-[#4CAF50] text-2xl`} />
                    </div>
                    <span className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">
                      {q.step}
                    </span>
                  </div>
                  <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-2">{q.title}</h3>
                  <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{q.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              認證資格
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              國際認可的品質認證
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              HACCP、ISO22000等認證，是康永生技品質承諾最有力的背書
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {certifications.map((cert) => (
              <div key={cert.name} className="flex gap-5 p-7 bg-[#f7faf7] rounded-2xl border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ backgroundColor: `${cert.color}15` }}
                >
                  <i className={`${cert.icon} text-2xl`} style={{ color: cert.color }} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">{cert.name}</h3>
                    <span className="text-gray-400 text-[0.75rem]">/ {cert.fullName}</span>
                  </div>
                  <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{cert.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Document support */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                文件支援
              </span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] leading-tight mb-5">
                生產證明與品質文件<br />全方位支援
              </h2>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                康永生技提供完整的品質文件支援，協助客戶應對市場查驗、通路上架審查及出口報關需求，讓您的品牌走得更遠、更穩。
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {docSupport.map((d) => (
                  <div key={d.text} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 border border-gray-100">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className={`${d.icon} text-[#4CAF50] text-lg`} />
                    </div>
                    <span className="text-[#1a2e1a] text-[0.875rem] font-medium">{d.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=quality%20control%20manager%20reviewing%20product%20batch%20records%20certificates%20documents%20in%20clean%20pharmaceutical%20laboratory%20office%2C%20professional%20QA%20QC%20documentation%20management%20biotech%20company&width=720&height=480&seq=prod-doc-01&orientation=landscape"
                  alt="品質文件支援"
                  className="w-full h-[320px] lg:h-[380px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ODM/OEM CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="bg-[#f0faf0] border border-green-100 rounded-3xl px-8 lg:px-14 py-12 text-center">
            <span className="inline-block bg-green-100 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
              適合 ODM / OEM 合作
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
              讓康永的專業生產能力<br />成為您品牌的堅實後盾
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
              無論是 ODM 客製配方代工或 OEM 指定配方生產，康永都能以最高品質標準完成您的委託。
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact/form"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
              >
                洽詢 ODM / OEM 合作
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/about/core-advantages"
                className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
              >
                了解核心優勢
              </Link>
            </div>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '核心優勢', desc: '天然選材、健康三贏、整合式服務與彈性客製方案，詳解康永四大優勢。', href: '/about/core-advantages' },
        { title: '法規風險', desc: '了解保健食品法規要點，康永協助您從一開始就站在合規基礎上。', href: '/about/regulation' },
        { title: '立即洽詢', desc: '填寫諮詢表單，了解康永如何為您的品牌提供生產支援。', href: '/contact/form' },
      ]} />
    </>
  );
}
